import React, { useCallback, useEffect, useMemo, useState } from "react";
import { WistiaPlayer } from "@wistia/wistia-player-react";

import { parseStrapiImage } from "utils";
import {
    PreviewImage,
    PreviewMediaLayer,
    PreviewPoster,
    PreviewRoot,
    PreviewVideo,
} from "./styled";

export default function CoursePreviewMedia({ active, imageUrl, previewVideo, title }) {
    const normalizedImageUrl = imageUrl ? parseStrapiImage(imageUrl) : null;
    const previewValue = `${previewVideo?.value || ""}`.trim();
    const [mediaReady, setMediaReady] = useState(false);

    const previewKey = useMemo(
        () => `${previewVideo?.sourceType || "image"}-${previewValue || "empty"}`,
        [previewVideo?.sourceType, previewValue]
    );

    useEffect(() => {
        setMediaReady(false);
    }, [active, previewKey]);

    const handleMediaReady = useCallback(() => {
        setMediaReady(true);
    }, []);

    const poster = normalizedImageUrl ? (
        <PreviewPoster visible={!active || !mediaReady}>
            <PreviewImage
                src={normalizedImageUrl}
                alt={title}
                loading="lazy"
                draggable={false}
            />
        </PreviewPoster>
    ) : null;

    if (active && previewVideo?.sourceType === "wistia" && previewValue) {
        return (
            <PreviewRoot data-testid="course-preview-wistia">
                {poster}
                <PreviewMediaLayer visible={mediaReady}>
                    <WistiaPlayer
                        key={previewKey}
                        mediaId={previewValue}
                        autoplay
                        bigPlayButtonBorderRadius={0}
                        muted
                        controlBarBorderRadius={0}
                        silentAutoplay="allow"
                        bigPlayButton={false}
                        controlsVisibleOnLoad={false}
                        fullscreenControl={false}
                        onApiReady={handleMediaReady}
                        onCanPlay={handleMediaReady}
                        onLoadedData={handleMediaReady}
                        playBarControl={false}
                        playbackRateControl={false}
                        playPauseControl={false}
                        qualityControl={false}
                        playerBorderRadius={0}
                        roundedPlayer={0}
                        settingsControl={false}
                        swatch={normalizedImageUrl || true}
                        poster={normalizedImageUrl || undefined}
                        style={{ width: "100%", height: "100%", borderRadius: 0 }}
                        volume={0}
                        volumeControl={false}
                    />
                </PreviewMediaLayer>
            </PreviewRoot>
        );
    }

    if (active && previewVideo?.sourceType === "url" && previewValue) {
        return (
            <PreviewRoot data-testid="course-preview-url">
                {poster}
                <PreviewMediaLayer visible={mediaReady}>
                    <PreviewVideo
                        autoPlay
                        key={previewKey}
                        loop
                        muted
                        onCanPlay={handleMediaReady}
                        onLoadedData={handleMediaReady}
                        playsInline
                        poster={normalizedImageUrl || undefined}
                        preload="metadata"
                    >
                        <source src={previewValue} />
                    </PreviewVideo>
                </PreviewMediaLayer>
            </PreviewRoot>
        );
    }

    if (normalizedImageUrl) {
        return (
            <PreviewRoot data-testid="course-preview-image">
                {poster}
            </PreviewRoot>
        );
    }

    return <PreviewRoot data-testid="course-preview-empty" aria-hidden="true" />;
}
